/**
 * Bridges that actually clear what they cross, and the structure under them.
 *
 * The map says which ways are bridges but not how high they are, and the
 * elevation model smooths away the cutting a motorway runs in and the
 * embankment a railway sits on. Draped naively, a bridge and the road under it
 * come out at nearly the same height and the two surfaces run through each
 * other. So this module:
 *
 * 1. **Finds every crossing** between a bridge's centreline and something it
 *    spans — a road, a railway, a waterway — by segment intersection over a
 *    spatial hash.
 * 2. **Raises the deck** wherever the gap is less than the clearance that
 *    thing needs (4.8 m over roads, 6.2 m over electrified rail, 2.7 m over a
 *    footpath), in a hump with a plateau over the crossing and ramps at a
 *    gradient the traffic could climb: 7 % for roads, 2.5 % for rail.
 * 3. **Carries the ramp into the approaches** when the bridge itself is too
 *    short to hold it, lifting the connected ways as an embankment would.
 * 4. **Builds the structure**: spans laid out so one is centred on each
 *    crossing, piers down to the ground, parapets and fascia. A railway's is
 *    an old stone bridge, a semi-elliptical arch per span with its soffit and
 *    spandrel walls up to the deck. A road's or a path's is the concrete slab
 *    bridge they are mostly built as now: a flat underside on abutment walls
 *    at its ends, with wing walls holding back the embankments behind them.
 *
 * Everything here is plain arrays and runs on the placed ribbons, so the
 * raised deck is also what the villagers walk on and what the player stands
 * on.
 */

import type { RailingIndex } from './railings.js';
import { ROAD_STYLE } from '../sources/shortbread.js';

/** Clearance from the surface below to the underside of the deck, by style. */
function clearanceOver(style: number, halfWidth: number): number {
  // A country stream runs under a low culvert bridge; only rivers and canals
  // need headroom for boats.
  // A bridge over a small river is barely above the banks — the water is
  // what is low, not the road — and a stream goes through a culvert.
  if (style === WATER) return halfWidth >= 3 ? 0.6 : 0.3;
  if (style === ROAD_STYLE.rail) return 6.2;
  // A tram runs under the same bridges as the street it is laid in.
  if (style <= ROAD_STYLE.service || style === ROAD_STYLE.tram) return 4.8;
  if (style === ROAD_STYLE.track) return 4.0;
  if (style === ROAD_STYLE.pedestrian) return 3.5;
  return 2.7;
}

/**
 * Under a deck over a way through an underpass: a car's height, and a van's.
 * The way under goes down to it, the road over stays level (`roadprofile.ts`).
 */
const UNDERPASS_CLEARANCE = 2.7;

/** Steepest gradient a bridge of this style ramps at. */
function gradeFor(style: number): number {
  if (style === ROAD_STYLE.rail) return 0.025;
  if (style === ROAD_STYLE.tram) return 0.06;
  // A fast road's deck rises the last of its clearance gently, as its own
  // profile would: at 7 %, the 0.7 m the motorway's deck lacked over the
  // railway under it at Landvetter was a hump in 10 m.
  if (style === ROAD_STYLE.motorway) return 0.02;
  if (style === ROAD_STYLE.arterial) return 0.03;
  if (style === ROAD_STYLE.collector) return 0.04;
  if (style <= ROAD_STYLE.track) return 0.07;
  return 0.12;
}

/** Stand-in style id for waterways passed in as things to be crossed. */
const WATER = 100;
/** How near a deck's surface a way's end is to be on the deck rather than under it, metres. See `meetRaisedRoads`. */
const ON_DECK = 1.0;
/** From the deck surface to the underside of its structure. */
export const DECK_DEPTH = 0.9;
const PARAPET = 1.0;
/** Along a deck's edge, how often a mapped railing beside it is looked for, metres. */
const RAILING_STEP = 0.5;
/** How far in from the deck's edge a parapet's wall collides, metres: under half a collider's thickness. */
const PARAPET_IN = 0.15;
/** A road bridge's edge beam over its deck, metres: the guardrail stands on it. */
const EDGE_BEAM = 0.25;
/** Road surface above a small stream it crosses by culvert, metres: barely a lift. */
const CULVERT = 0.4;
/** Crossings this close to a bridge's end are junctions with its approach. */
const JUNCTION = 3;
/**
 * Most a deck is ever raised. Beyond this the map and the elevation model
 * disagree too badly to trust — a road "under" a bridge that the terrain puts
 * on a hill above it — and a ten-metre hump is worse than the clash.
 */
const MAX_RAISE = 8;
/** Steepest a deck's raise is ramped down to the tile's edge when it must reach the ground there: see `carry`. */
const EDGE_GRADE = 0.15;
/** Longest span before another pier is added. */
const MAX_SPAN = 24;
/**
 * Shallowest crossing angle, as its sine, a span is laid out for: about 15°.
 * Shallower, the road under runs along the deck rather than across it.
 */
const SKEW_SIN = 0.25;
/** Least room from a pier to whatever passes under the deck, past its edge, metres. */
const PIER_CLEAR = 1;
/** A road's kerbs, a railway's ballast shoulder: past the half-width a pier keeps clear of too. */
const VERGE = 0.5;
/** Least gap under a deck for something to count as passing beneath it, metres. */
const BENEATH = 1;
/** Where a pier is tried, from where the layout puts it: nearest first, either way, up to half a span. */
const PIER_SHIFTS = [0, ...Array.from({ length: MAX_SPAN * 2 }, (_, i) => (i % 2 === 0 ? 1 : -1) * 0.5 * (Math.floor(i / 2) + 1))];

interface Section {
  /** Vertex index of the left edge; the right edge is `a + 1`. */
  a: number;
  distance: number;
  x: number;
  z: number;
}

interface Run {
  sections: Section[];
  style: number;
  halfWidth: number;
  bridge: boolean;
  tunnel: boolean;
  /** A way through an underpass (`tunnels.ts`), in its trough. */
  underpass?: boolean;
}

/** Split a ribbon's quads into runs of consecutive cross-sections, one per way. */
function runsOf(
  positions: Float32Array,
  indices: Uint32Array,
  attributes: Float32Array | undefined,
  bridges: Uint8Array | undefined,
  defaultStyle: number,
  defaultHalfWidth: number,
  underpasses?: Uint8Array,
): Run[] {
  const runs: Run[] = [];
  const quads = Math.floor(indices.length / 6);
  let first = -1;
  let previous = -1;
  const flush = (a0: number, aLast: number) => {
    const sections: Section[] = [];
    let travelled = 0;
    for (let a = a0; a <= aLast + 2; a += 2) {
      const x = (positions[a * 3] + positions[(a + 1) * 3]) / 2;
      const z = (positions[a * 3 + 2] + positions[(a + 1) * 3 + 2]) / 2;
      if (sections.length > 0) {
        const p = sections[sections.length - 1];
        travelled += Math.hypot(x - p.x, z - p.z);
      }
      sections.push({ a, distance: travelled, x, z });
    }
    runs.push({
      sections,
      style: attributes !== undefined ? Math.round(attributes[a0 * 4 + 2]) : defaultStyle,
      // Waterways carry no attributes: measure the width off the ribbon itself.
      halfWidth: attributes !== undefined
        ? attributes[a0 * 4 + 3]
        : Math.hypot(positions[a0 * 3] - positions[(a0 + 1) * 3], positions[a0 * 3 + 2] - positions[(a0 + 1) * 3 + 2]) / 2
          || defaultHalfWidth,
      bridge: bridges !== undefined && bridges[a0] === 1,
      tunnel: bridges !== undefined && bridges[a0] === 2,
      underpass: underpasses?.[a0] === 1,
    });
  };
  for (let q = 0; q < quads; q++) {
    const a = indices[q * 6];
    if (first < 0) first = a;
    else if (a !== previous + 2) {
      flush(first, previous);
      first = a;
    }
    previous = a;
  }
  if (first >= 0) flush(first, previous);
  return runs;
}

/** Height of a run's deck at a cross-section, the mean of its two edges. */
function heightAt(positions: Float32Array, a: number): number {
  return (positions[a * 3 + 1] + positions[(a + 1) * 3 + 1]) / 2;
}

interface Crossing {
  distance: number;
  /** How much the deck must rise here. */
  raise: number;
  /** Half the width of what is crossed, for laying out the span over it. */
  spanHalf: number;
  /** Which under-run it is, to merge a crossing found twice. */
  under: number;
  /** Over a way through an underpass: its trough's walls are the abutments. */
  underpass?: boolean;
}

export interface BridgeInput {
  /**
   * OpenStreetMap's railings on bridges: where one runs along a deck's edge
   * the deck reaches out to it, and the game's own parapet is left out there.
   */
  railings?: RailingIndex;
  roadPositions: Float32Array;
  roadIndices: Uint32Array;
  /**
   * Every road quad, the strip of the neighbours' roads past the tile's edge
   * included, for what passes under a deck. A deck is drawn whole, so it can
   * reach over roads `roadIndices` has cut away. Defaults to `roadIndices`.
   */
  underIndices?: Uint32Array;
  roadAttributes: Float32Array;
  roadBridges: Uint8Array;
  /** Per vertex: 1 on a way through an underpass, in its trough (`tunnels.ts`). */
  roadUnderpasses?: Uint8Array;
  waterPositions: Float32Array;
  waterIndices: Uint32Array;
  /** Mapped water polygons, including canals with no waterway centreline. */
  waterLevel?: (x: number, z: number) => number | undefined;
  /** Terrain height at chunk-local (x, z). */
  ground: (x: number, z: number) => number;
  /**
   * The tile's size, metres. A ramp carried into an approach is back on the
   * ground by the tile's edge, where the neighbour, which may not hold this
   * bridge, has the road on the ground too. Unbounded by default.
   */
  width?: number;
  depth?: number;
  /**
   * Whether (x, z) is on a bridge mapped as an area: a way on one is laid on
   * its deck and builds nothing of its own (`bridgeareas.ts`).
   */
  inArea?: (x: number, z: number) => boolean;
  /** Where no embankment comes down: a trough's walls hold the bank there. */
  keepOut?: (x: number, z: number) => boolean;
  /** Linear RGB of the local ground, for the embankments under raised approaches. */
  embankment?: [number, number, number] | ((x: number, z: number) => [number, number, number]);
}

export interface BridgeStructure {
  /** x, y, z triples. */
  positions: Float32Array;
  /** Linear RGB per vertex. */
  colors: Float32Array;
  indices: Uint32Array;
  /**
   * The parapets of path and railway bridges, to collide with: x0, z0, x1,
   * z1, bottom, top per piece, as `ColliderInput.walls`. A road's deck has
   * its guardrail's instead (`guardrails.ts`).
   */
  walls: Float32Array;
  /**
   * The decks widened out to their mapped railings: drawn with the rest, and
   * here on their own as well, as ground to walk on.
   */
  widened: { positions: Float32Array; colors: Float32Array; indices: Uint32Array };
}

const CELL = 20;
const cellKey = (cx: number, cz: number) => cx * 92821 + cz;

/**
 * Raise every bridge to clear what it crosses — mutating `roadPositions` in
 * place — and return the structure to draw beneath and beside the decks.
 */
export function buildBridges(input: BridgeInput): BridgeStructure {
  const P = input.roadPositions;
  const extra = new Float32Array(P.length / 3);
  // Not tunnels: under the ground, nothing here is over them or meets them.
  const roads = runsOf(P, input.roadIndices, input.roadAttributes, input.roadBridges, 0, 0, input.roadUnderpasses).filter(r => !r.tunnel);
  const waters = runsOf(input.waterPositions, input.waterIndices, undefined, undefined, WATER, 2);
  const edge = tileEdge(input.width ?? Infinity, input.depth ?? Infinity);
  const below = input.underIndices === undefined ? roads
    : runsOf(P, input.underIndices, input.roadAttributes, input.roadBridges, 0, 0, input.roadUnderpasses).filter(r => !r.tunnel);

  // Everything a bridge can pass over, as centreline segments in a hash.
  // Not a street's tram rails: a bridge running into a square meets them at
  // grade, and one over the street they lie in clears the street itself.
  const unders: Array<{ run: Run; positions: Float32Array }> = [
    ...below.filter(r => r.style !== ROAD_STYLE.tram).map(run => ({ run, positions: P })),
    ...waters.map(run => ({ run, positions: input.waterPositions })),
  ];
  const hash = new Map<number, Array<[number, number]>>();
  unders.forEach((u, ui) => {
    const s = u.run.sections;
    for (let i = 0; i + 1 < s.length; i++) {
      const cx0 = Math.floor(Math.min(s[i].x, s[i + 1].x) / CELL);
      const cx1 = Math.floor(Math.max(s[i].x, s[i + 1].x) / CELL);
      const cz0 = Math.floor(Math.min(s[i].z, s[i + 1].z) / CELL);
      const cz1 = Math.floor(Math.max(s[i].z, s[i + 1].z) / CELL);
      for (let cx = cx0; cx <= cx1; cx++) {
        for (let cz = cz0; cz <= cz1; cz++) {
          const key = cellKey(cx, cz);
          let list = hash.get(key);
          if (list === undefined) hash.set(key, (list = []));
          list.push([ui, i]);
        }
      }
    }
  });

  const onArea = (r: Run) => {
    const m = r.sections[r.sections.length >> 1];
    return input.inArea?.(m.x, m.z) === true;
  };
  // Solve lower decks first so an upper deck clears their final surface.
  // The source does not always supply OSM layers: use the designed elevation,
  // and only treat clearly separated decks as stacked, never adjoining ways
  // on one bridge. These reference heights stay fixed while lifts accumulate.
  const deckLevel = (r: Run) => r.sections.reduce((sum, s) => sum + heightAt(P, s.a), 0) / r.sections.length;
  const bridges = roads.filter(r => r.bridge && r.sections.length >= 2 && !onArea(r)).sort((a, b) => deckLevel(a) - deckLevel(b));
  const crossingsFor = new Map<Run, Crossing[]>();
  // Where each way under a deck ends, to tell a road the map split under it
  // from one that ends there.
  const endsAt = new Map<number, Run[]>();
  const endKey = (x: number, z: number) => cellKey(Math.round(x), Math.round(z));
  for (const { run } of unders) {
    for (const e of [run.sections[0], run.sections[run.sections.length - 1]]) {
      const key = endKey(e.x, e.z);
      endsAt.set(key, [...(endsAt.get(key) ?? []), run]);
    }
  }
  const runsOn = (run: Run, end: Section) => {
    for (let dx = -1; dx <= 1; dx++) {
      for (let dz = -1; dz <= 1; dz++) {
        for (const other of endsAt.get(endKey(end.x + dx, end.z + dz)) ?? []) {
          if (other === run || other.style !== run.style) continue;
          const os = other.sections;
          if ([os[0], os[os.length - 1]].some(o => Math.hypot(o.x - end.x, o.z - end.z) < 0.5)) return true;
        }
      }
    }
    return false;
  };

  // --- 1 & 2: find crossings and raise decks -------------------------------
  for (const bridge of bridges) {
    const crossings: Crossing[] = [];
    const s = bridge.sections;
    const seen = new Set<string>();
    for (let i = 0; i + 1 < s.length; i++) {
      const ax = s[i].x, az = s[i].z, bx = s[i + 1].x, bz = s[i + 1].z;
      const cx0 = Math.floor(Math.min(ax, bx) / CELL), cx1 = Math.floor(Math.max(ax, bx) / CELL);
      const cz0 = Math.floor(Math.min(az, bz) / CELL), cz1 = Math.floor(Math.max(az, bz) / CELL);
      for (let cx = cx0; cx <= cx1; cx++) {
        for (let cz = cz0; cz <= cz1; cz++) {
          for (const [ui, j] of hash.get(cellKey(cx, cz)) ?? []) {
            // Per bridge segment: a pair can straddle several cells, but a
            // pair missed by one bridge segment must still be tried by the next.
            const tag = `${i}:${ui}:${j}`;
            if (seen.has(tag)) continue;
            seen.add(tag);
            const u = unders[ui];
            const us = u.run.sections;
            const hit = intersect(ax, az, bx, bz, us[j].x, us[j].z, us[j + 1].x, us[j + 1].z);
            if (hit === undefined) continue;
            // A junction is not a crossing. A road that meets the bridge at its
            // end, or ends against it, touches it without passing beneath —
            // and treating it as beneath lifts the bridge end and its whole
            // approach metres into the air.
            const along = s[i].distance + (s[i + 1].distance - s[i].distance) * hit.t;
            const bridgeLength = s[s.length - 1].distance;
            if (along < JUNCTION || along > bridgeLength - JUNCTION) continue;
            // A road ending under the deck ends against it — unless the map
            // only split it there, and another way runs on from its end.
            const underAlong = us[j].distance + (us[j + 1].distance - us[j].distance) * hit.u;
            const atEnd = underAlong < 1.5 ? us[0] : underAlong > us[us.length - 1].distance - 1.5 ? us[us.length - 1] : undefined;
            if (atEnd !== undefined && !runsOn(u.run, atEnd)) continue;
            const deck = heightAt(P, s[i].a) + (heightAt(P, s[i + 1].a) - heightAt(P, s[i].a)) * hit.t;
            const baseBelow = heightAt(u.positions, us[j].a) + (heightAt(u.positions, us[j + 1].a) - heightAt(u.positions, us[j].a)) * hit.u;
            // A deck can cross another deck. Previously every bridge was
            // excluded here, so flyovers intersected and piers blocked the
            // lower carriageway. Preserve the original vertical order.
            if (u.run.bridge && (deck - baseBelow < 1.5 || deckLevel(u.run) >= deckLevel(bridge))) continue;
            const below = baseBelow + (u.run.bridge ? extra[us[j].a] + (extra[us[j + 1].a] - extra[us[j].a]) * hit.u : 0);
            // Water is measured to the road surface, not the deck's underside:
            // a stream runs through a culvert and a small river under a low
            // bridge whose banks are cut down to it, so neither lifts the road
            // more than a little. Only roads and railways need true headroom.
            const water = u.run.style === WATER;
            const culvert = water && u.run.halfWidth < 3;
            const needed = Math.min(
              culvert ? below + CULVERT
                : below + (u.run.underpass === true ? UNDERPASS_CLEARANCE : clearanceOver(u.run.style, u.run.halfWidth)) + (water ? 0 : DECK_DEPTH),
              deck + MAX_RAISE,
            );
            const distance = s[i].distance + (s[i + 1].distance - s[i].distance) * hit.t;
            // The same crossing found from both segments that share its vertex.
            const twin = crossings.find(c => c.under === ui && Math.abs(c.distance - distance) < 1);
            if (twin !== undefined) {
              twin.raise = Math.max(twin.raise, needed - deck);
              continue;
            }
            // Along the deck, what it crosses takes more room the shallower
            // the angle: its own width over the sine, and the deck's width
            // over the tangent, as each edge of the deck meets it elsewhere.
            const sin = Math.max(SKEW_SIN, Math.abs(hit.sin));
            crossings.push({
              distance,
              raise: Math.max(0, needed - deck),
              spanHalf: u.run.halfWidth / sin + bridge.halfWidth * Math.sqrt(1 - sin * sin) / sin + 2.5,
              under: ui,
              underpass: u.run.underpass === true,
            });
          }
        }
      }
    }
    // Polygon water also counts when OSM has no centreline. Raise the whole
    // deck together so many water samples cannot introduce humps in the span.
    let waterRaise = 0;
    for (let i = 0; i + 1 < s.length; i++) {
      const steps = Math.max(1, Math.ceil((s[i + 1].distance - s[i].distance) / 2));
      for (let k = 0; k <= steps; k++) {
        const t = k / steps;
        const level = input.waterLevel?.(s[i].x + (s[i + 1].x - s[i].x) * t, s[i].z + (s[i + 1].z - s[i].z) * t);
        if (level === undefined) continue;
        const deck = heightAt(P, s[i].a) + (heightAt(P, s[i + 1].a) - heightAt(P, s[i].a)) * t;
        waterRaise = Math.max(waterRaise, Math.min(MAX_RAISE, level + 0.6 - deck));
      }
    }
    if (waterRaise > 0) crossings.push({ distance: s[s.length - 1].distance / 2,
      spanHalf: s[s.length - 1].distance / 2, raise: waterRaise, under: -1 });
    crossingsFor.set(bridge, crossings);

    const grade = gradeFor(bridge.style);
    // No more than can ramp back down before the tile's edge, where the road
    // is cut at the height both tiles agree on and nothing lifts it, even
    // squeezed to `EDGE_GRADE`. Lifted over an underpass right by the edge, a
    // motorway's deck stood 2.6 m over the cut 0.7 m on: a cliff across the
    // carriageway. Short of clearance is the lesser evil there.
    const length0 = s[s.length - 1].distance;
    const edgeAt = (atStart: boolean): number => {
      const own = edge(bridge, atStart);
      if (Number.isFinite(own)) return atStart ? own : length0 - own;
      const end = atStart ? s[0] : s[s.length - 1];
      let room = Infinity;
      for (const run of roads) {
        if (run === bridge) continue;
        const r = run.sections;
        if (Math.hypot(r[0].x - end.x, r[0].z - end.z) < 2.5) room = Math.min(room, edge(run, true));
        else if (Math.hypot(r[r.length - 1].x - end.x, r[r.length - 1].z - end.z) < 2.5) room = Math.min(room, edge(run, false));
      }
      return atStart ? -room : length0 + room;
    };
    const edge0 = edgeAt(true), edge1 = edgeAt(false);
    // From each crossing's plateau to the edge, either way along the deck.
    const room = (c: Crossing, before: boolean) => before
      ? (edge0 < c.distance ? Math.max(0, c.distance - c.spanHalf - edge0) : Infinity)
      : (edge1 > c.distance ? Math.max(0, edge1 - c.distance - c.spanHalf) : Infinity);
    for (const c of crossings) c.raise = Math.min(c.raise, EDGE_GRADE * room(c, true), EDGE_GRADE * room(c, false));
    const raiseAt = (d: number) => {
      let r = 0;
      for (const c of crossings) {
        if (c.raise <= 0) continue;
        const excess = Math.max(0, Math.abs(d - c.distance) - c.spanHalf);
        r = Math.max(r, rampOver(c.raise, excess, Math.min(c.raise / grade, room(c, d < c.distance))));
      }
      return r;
    };
    for (const sec of s) lift(extra, sec.a, raiseAt(sec.distance));

    // --- 3: carry what is left of the ramp into the approaches ---------------
    const length = s[s.length - 1].distance;
    for (const [end, residual] of [[s[0], raiseAt(0)], [s[s.length - 1], raiseAt(length)]] as const) {
      if (residual > 0.05) carry(extra, roads, bridge, end.x, end.z, residual, grade, 0, edge);
    }
  }
  meetRaisedRoads(roads, P, extra, edge);
  for (let v = 0; v < extra.length; v++) P[v * 3 + 1] += extra[v];
  const decks = new Decks(P, bridges);
  const roadDecks = new Decks(P, bridges.filter(b => b.style <= ROAD_STYLE.service));
  // Tram rails on a road's bridge lie on its deck, not on a level of their own.
  for (const bridge of bridges) {
    if (bridge.style !== ROAD_STYLE.tram) continue;
    for (const sec of bridge.sections) {
      for (const v of [sec.a, sec.a + 1]) {
        const on = roadDecks.at(P[v * 3], P[v * 3 + 2], bridge);
        if (on !== undefined && Math.abs(on.h - P[v * 3 + 1]) < 1.5) P[v * 3 + 1] = on.h + TRAM_ON_DECK;
      }
    }
  }

  // Embankments: a raised approach is carried on a bank of earth sloping down
  // to the ground at 1 in 1.5 either side, not floating on nothing.
  const out = { positions: [] as number[], colors: [] as number[], indices: [] as number[] };
  const earth = input.embankment ?? [0.1, 0.3, 0.05];
  for (const run of roads) {
    if (run.bridge) continue;
    const sec = run.sections;
    for (let i = 0; i + 1 < sec.length; i++) {
      const a = sec[i].a, b = sec[i + 1].a;
      if (extra[a] < 0.2 && extra[b] < 0.2) continue;
      for (const [edge, other] of [[0, 1], [1, 0]] as const) {
        const foot = (v: number, lift: number) => {
          const ex = P[(v + edge) * 3], ez = P[(v + edge) * 3 + 2];
          const ox = ex - P[(v + other) * 3], oz = ez - P[(v + other) * 3 + 2];
          const len = Math.hypot(ox, oz) || 1;
          const reach = lift * 1.5 + 0.3;
          const fx = ex + (ox / len) * reach, fz = ez + (oz / len) * reach;
          return [fx, input.ground(fx, fz) - 0.3, fz];
        };
        const topA = [P[(a + edge) * 3], P[(a + edge) * 3 + 1] - 0.05, P[(a + edge) * 3 + 2]];
        const topB = [P[(b + edge) * 3], P[(b + edge) * 3 + 1] - 0.05, P[(b + edge) * 3 + 2]];
        const footA = foot(a, extra[a]);
        const footB = foot(b, extra[b]);
        if (input.keepOut !== undefined && (input.keepOut(footA[0], footA[2]) || input.keepOut(footB[0], footB[2]))) continue;
        const base = out.positions.length / 3;
        out.positions.push(...topA, ...topB, ...footB, ...footA);
        const tone = typeof earth === 'function' ? earth((topA[0] + topB[0]) / 2, (topA[2] + topB[2]) / 2) : earth;
        for (let k = 0; k < 4; k++) out.colors.push(tone[0], tone[1], tone[2]);
        out.indices.push(base, base + 1, base + 2, base, base + 2, base + 3);
      }
    }
  }

  // --- 4: the structure -------------------------------------------------------
  // What a pier must not stand in: every road, railway, path, tram line and
  // waterway, and every other deck, at whatever height it ends up.
  const obstacles = new Obstacles([
    ...below.filter(r => !r.bridge).map(run => ({ run, positions: P })),
    ...bridges.map(run => ({ run, positions: P })),
    ...waters.map(run => ({ run, positions: input.waterPositions })),
  ]);
  const walls: number[] = [];
  const widened = { positions: [] as number[], colors: [] as number[], indices: [] as number[] };
  for (const bridge of bridges) {
    buildStructure(out, P, bridge, crossingsFor.get(bridge) ?? [], input.ground, decks, obstacles, walls, input.railings, widened);
  }
  return {
    positions: new Float32Array(out.positions),
    colors: new Float32Array(out.colors),
    indices: new Uint32Array(out.indices),
    walls: new Float32Array(walls),
    widened: { positions: new Float32Array(widened.positions), colors: new Float32Array(widened.colors), indices: new Uint32Array(widened.indices) },
  };
}

/** A ramp that eases out over `length` metres. */
function rampOver(raise: number, excess: number, length: number): number {
  if (excess >= length || length <= 0) return 0;
  const t = 1 - excess / length;
  return raise * t * t * (3 - 2 * t);
}

/**
 * How far along `run`, from the end at (x, z), it first crosses another road
 * or a railway on the level, less that road's half-width and a metre: where a
 * ramp along it has to be back down at the ground. A path to a footbridge
 * that crosses a street or a slip road first meets it at its own surface;
 * carried on up, its bank of earth is a dam across the road. Undefined if it
 * crosses none.
 */
function groundCrossing(run: Run, roads: Run[], fromStart: boolean): number | undefined {
  const s = run.sections;
  const length = s[s.length - 1].distance;
  let first: number | undefined;
  for (const other of roads) {
    if (other === run || other.bridge || other.style === ROAD_STYLE.tram) continue;
    if (other.style > ROAD_STYLE.service && other.style !== ROAD_STYLE.rail) continue;
    const o = other.sections;
    const otherLength = o[o.length - 1].distance;
    for (let i = 0; i + 1 < s.length; i++) {
      for (let j = 0; j + 1 < o.length; j++) {
        const hit = intersect(s[i].x, s[i].z, s[i + 1].x, s[i + 1].z, o[j].x, o[j].z, o[j + 1].x, o[j + 1].z);
        if (hit === undefined) continue;
        const along = s[i].distance + (s[i + 1].distance - s[i].distance) * hit.t;
        const fromEnd = fromStart ? along : length - along;
        // Not at this end, where the ramp starts: two slip roads forking off
        // a deck's end meet there, and the junction is not a road crossed.
        // Held to it, neither was lifted, and both stopped 2 m under the deck
        // at Örgrytemotet. Nor where the other road ends: it joins this one,
        // and is brought up to it (`meetRaisedRoads`), not crossed on the
        // level. A 6 m stub from a deck's end to the road starting at its
        // far end came down 2.7 m in its last 2 m.
        if (fromEnd < JUNCTION) continue;
        const alongOther = o[j].distance + (o[j + 1].distance - o[j].distance) * hit.u;
        if (alongOther < 1.5 || alongOther > otherLength - 1.5) continue;
        const d = fromEnd - other.halfWidth - 1;
        if (first === undefined || d < first) first = Math.max(0, d);
      }
    }
    // Or ends against it: a T-junction, the way's far end at the other
    // road's kerb, which runs on past. The ramp is down at that kerb, however
    // steep: the slip road onto a 20 m deck over a path at Örgrytemotet, 13 m
    // from Örgrytevägen's kerb, was carried at a motorway's 2 % and stood
    // 2.1 m over the street it leaves, and the carry ran on into the
    // street's own links. Beside it, at the nearest point of its whole line:
    // a road starting at this end is near its own later sections along its
    // axis, and is a continuation, not a road run into.
    const far = fromStart ? s[s.length - 1] : s[0];
    let dist = Infinity, alongOther = 0;
    for (let j = 0; j + 1 < o.length; j++) {
      const near = nearestOn(far.x, far.z, o[j].x, o[j].z, o[j + 1].x, o[j + 1].z);
      if (near.dist >= dist) continue;
      dist = near.dist;
      alongOther = o[j].distance + (o[j + 1].distance - o[j].distance) * near.along;
    }
    if (dist > other.halfWidth + 1 || alongOther < 1.5 || alongOther > otherLength - 1.5) continue;
    const d = length - (other.halfWidth + 1 - dist);
    if (first === undefined || d < first) first = Math.max(0, d);
  }
  return first;
}

/** Distance from (x, z) to the segment a–b, and the parameter along it of the nearest point. */
function nearestOn(x: number, z: number, ax: number, az: number, bx: number, bz: number): { dist: number; along: number } {
  const rx = bx - ax, rz = bz - az;
  const len2 = rx * rx + rz * rz;
  const along = len2 > 0 ? Math.min(1, Math.max(0, ((x - ax) * rx + (z - az) * rz) / len2)) : 0;
  return { dist: Math.hypot(x - (ax + rx * along), z - (az + rz * along)), along };
}

/** How far along a run, from one end, it reaches the tile's edge: the most a ramp along it may run. */
type TileEdge = (run: Run, fromStart: boolean) => number;

function tileEdge(width: number, depth: number): TileEdge {
  const on = (sec: Section) => Math.min(sec.x, sec.z, width - sec.x, depth - sec.z) < 0.05;
  return (run, fromStart) => {
    const s = run.sections, length = s[s.length - 1].distance;
    for (let i = 0; i < s.length; i++) {
      const sec = s[fromStart ? i : s.length - 1 - i];
      if (on(sec)) return fromStart ? sec.distance : length - sec.distance;
    }
    return Infinity;
  };
}

/**
 * Record a raise for a cross-section. Raises combine by maximum, not sum, so
 * an approach shared by two bridges is lifted to the higher of the two ramps
 * rather than both stacked. Applied to the positions once, at the end.
 */
function lift(extra: Float32Array, a: number, by: number): void {
  if (by <= 0) return;
  extra[a] = Math.max(extra[a], by);
  extra[a + 1] = Math.max(extra[a + 1], by);
}

/**
 * Lift the ways that meet a bridge end, as an embankment would, by a ramp
 * falling away from the joint. Recurses along short approaches that cannot
 * hold the whole ramp, a few ways deep at most.
 */
function carry(extra: Float32Array, roads: Run[], from: Run, x: number, z: number, raise: number, grade: number, depth: number, edge: TileEdge): void {
  if (depth > 3) return;
  for (const run of roads) {
    if (run === from) continue;
    const s = run.sections;
    const startNear = Math.hypot(s[0].x - x, s[0].z - z) < 2.5;
    const endNear = Math.hypot(s[s.length - 1].x - x, s[s.length - 1].z - z) < 2.5;
    if (!startNear && !endNear) continue;
    const length = s[s.length - 1].distance;
    // Down to the ground before any road it crosses on the level, steeper if
    // it must be, and no further.
    const reach = Math.min(raise / grade, groundAlong(run, roads, startNear, edge));
    for (const sec of s) {
      const d = startNear ? sec.distance : length - sec.distance;
      lift(extra, sec.a, rampOver(raise, d, reach));
    }
    const left = rampOver(raise, length, reach);
    if (left > 0.05) {
      const far = startNear ? s[s.length - 1] : s[0];
      carry(extra, roads, run, far.x, far.z, left, grade, depth + 1, edge);
    }
  }
}

/**
 * How far a ramp along `run` from the end given may go before it must be
 * down at the ground: to the first road crossed on the level or ended
 * against (`groundCrossing`), or the tile's edge — along the way, and on
 * along whatever runs on from its far end. The whole chain shares the
 * descent: a 6 m stub from a deck's end to a 19 m ramp onto a street was
 * carried level and the ramp alone came down 3.9 m, at 36 %.
 */
function groundAlong(run: Run, roads: Run[], fromStart: boolean, edge: TileEdge, depth = 0): number {
  const s = run.sections;
  const own = Math.min(groundCrossing(run, roads, fromStart) ?? Infinity, edge(run, fromStart));
  if (Number.isFinite(own) || depth > 3) return own;
  const length = s[s.length - 1].distance;
  const far = fromStart ? s[s.length - 1] : s[0];
  let on = Infinity;
  for (const next of roads) {
    if (next === run || next.bridge) continue;
    const t = next.sections;
    const startNear = Math.hypot(t[0].x - far.x, t[0].z - far.z) < 2.5;
    const endNear = Math.hypot(t[t.length - 1].x - far.x, t[t.length - 1].z - far.z) < 2.5;
    if (!startNear && !endNear) continue;
    on = Math.min(on, groundAlong(next, roads, startNear, edge, depth + 1));
  }
  return length + on;
}

/**
 * Bring side roads up to meet raised ones.
 *
 * A way that ends against a road partway up its ramp — a farm entrance, a
 * T-junction — would otherwise stop dead against a kerb a metre high. Each such
 * end takes the raise of the section it meets and ramps back down along its own
 * way, at its own gradient, and on into whatever continues from it. Repeated a
 * few times so a chain of short ways settles.
 */
function meetRaisedRoads(roads: Run[], P: Float32Array, extra: Float32Array, edge: TileEdge): void {
  const CELLM = 10;
  const sections = new Map<number, Array<{ run: Run; a: number; x: number; z: number }>>();
  for (const run of roads) {
    for (const sec of run.sections) {
      const key = cellKey(Math.floor(sec.x / CELLM), Math.floor(sec.z / CELLM));
      let list = sections.get(key);
      if (list === undefined) sections.set(key, (list = []));
      list.push({ run, a: sec.a, x: sec.x, z: sec.z });
    }
  }
  for (let pass = 0; pass < 3; pass++) {
    let changed = false;
    for (const run of roads) {
      const s = run.sections;
      for (const atStart of [true, false]) {
        const end = atStart ? s[0] : s[s.length - 1];
        let meet = 0;
        const cx = Math.floor(end.x / CELLM), cz = Math.floor(end.z / CELLM);
        for (let dx = -1; dx <= 1; dx++) {
          for (let dz = -1; dz <= 1; dz++) {
            for (const other of sections.get(cellKey(cx + dx, cz + dz)) ?? []) {
              if (other.run === run) continue;
              // Not a deck a way ends under: it passes beneath it, and the
              // ways onto a deck's ends are carried up already. But an end on
              // a deck's surface is on it, and rises with it: a slip road's
              // own deck merging onto a flyover halfway along, or the road
              // on from a deck's end. A deck itself meets only what it ends on.
              const on = Math.abs(heightAt(P, other.a) - heightAt(P, end.a)) < ON_DECK;
              if ((other.run.bridge || run.bridge) && !on) continue;
              // A deck's raise ramps along it: an end on one takes the raise
              // at its own cross-section there, not a neighbour's up the ramp.
              if (Math.hypot(other.x - end.x, other.z - end.z) > (other.run.bridge ? 2 : other.run.halfWidth + 2)) continue;
              meet = Math.max(meet, extra[other.a]);
            }
          }
        }
        if (meet - extra[end.a] < 0.05) continue;
        const grade = gradeFor(run.style);
        const length = s[s.length - 1].distance;
        // A deck ramps however far it must: what it crosses it passes over.
        const reach = Math.min(meet / grade, run.bridge ? edge(run, atStart) : groundAlong(run, roads, atStart, edge));
        for (const sec of s) {
          const d = atStart ? sec.distance : length - sec.distance;
          const r = rampOver(meet, d, reach);
          if (r > extra[sec.a] + 1e-4) {
            lift(extra, sec.a, r);
            changed = true;
          }
        }
      }
    }
    if (!changed) break;
  }
}

/** Segment intersection in 2D: parameters along each, or undefined. */
function intersect(
  ax: number, az: number, bx: number, bz: number,
  cx: number, cz: number, dx: number, dz: number,
): { t: number; u: number; sin: number } | undefined {
  const rx = bx - ax, rz = bz - az, sx = dx - cx, sz = dz - cz;
  const denom = rx * sz - rz * sx;
  if (Math.abs(denom) < 1e-9) return undefined;
  const t = ((cx - ax) * sz - (cz - az) * sx) / denom;
  const u = ((cx - ax) * rz - (cz - az) * rx) / denom;
  // A little slack: both lines are resampled on a metre grid, so crossings
  // often fall exactly on a vertex, and float32 rounding would otherwise put
  // the parameter a hair outside [0, 1] and lose the crossing entirely.
  const e = 1e-3;
  if (t < -e || t > 1 + e || u < -e || u > 1 + e) return undefined;
  return { t: Math.min(1, Math.max(0, t)), u: Math.min(1, Math.max(0, u)), sin: denom / (Math.hypot(rx, rz) * Math.hypot(sx, sz)) };
}

// --- structure ------------------------------------------------------------------

const STONE = [0.39, 0.35, 0.29];
const CONCRETE = [0.5, 0.48, 0.44];
/** The deck slab between neighbouring decks, and under a tram's bare rails. */
const SLAB = [0.4, 0.39, 0.37];
/** Tram rails over the deck of the bridge they share, metres. */
const TRAM_ON_DECK = 0.02;
/**
 * Widest gap between two decks side by side that is filled in, metres. OSM
 * maps a city bridge's carriageway, tramway, cycleway and footways as
 * separate bridges; built apart, each had its own parapets with the canal
 * showing between them. Within this they are one bridge: no walls between,
 * and the gap decked over.
 */
const CLUSTER_GAP = 5;
/** Slabs sit this far under the decks they join, so the ribbons draw over them. */
const SLAB_UNDER = 0.04;

/** Bridge decks by grid cell, to find the one beside or under a point. */
class Decks {
  private readonly cells = new Map<number, Array<[Run, number]>>();
  private static readonly CELL = 8;
  constructor(private readonly P: Float32Array, bridges: Run[]) {
    const C = Decks.CELL;
    for (const run of bridges) {
      const s = run.sections;
      for (let i = 0; i + 1 < s.length; i++) {
        const r = run.halfWidth + 0.5;
        for (let cx = Math.floor((Math.min(s[i].x, s[i + 1].x) - r) / C); cx <= Math.floor((Math.max(s[i].x, s[i + 1].x) + r) / C); cx++) {
          for (let cz = Math.floor((Math.min(s[i].z, s[i + 1].z) - r) / C); cz <= Math.floor((Math.max(s[i].z, s[i + 1].z) + r) / C); cz++) {
            const key = cellKey(cx, cz);
            let list = this.cells.get(key);
            if (list === undefined) this.cells.set(key, (list = []));
            list.push([run, i]);
          }
        }
      }
    }
  }

  /** The highest deck other than `self` at (x, z): its surface and unit direction. */
  at(x: number, z: number, self: Run): { h: number; dx: number; dz: number } | undefined {
    let best: { h: number; dx: number; dz: number } | undefined;
    for (const [run, i] of this.cells.get(cellKey(Math.floor(x / Decks.CELL), Math.floor(z / Decks.CELL))) ?? []) {
      if (run === self) continue;
      const a = run.sections[i], b = run.sections[i + 1];
      const ex = b.x - a.x, ez = b.z - a.z, len2 = ex * ex + ez * ez;
      if (len2 < 1e-6) continue;
      const t = Math.max(0, Math.min(1, ((x - a.x) * ex + (z - a.z) * ez) / len2));
      if (Math.hypot(x - a.x - ex * t, z - a.z - ez * t) > run.halfWidth + 0.05) continue;
      const h = heightAt(this.P, a.a) + (heightAt(this.P, b.a) - heightAt(this.P, a.a)) * t;
      const len = Math.sqrt(len2);
      if (best === undefined || h > best.h) best = { h, dx: ex / len, dz: ez / len };
    }
    return best;
  }
}

interface Edge {
  lx: number; ly: number; lz: number;
  rx: number; ry: number; rz: number;
}

/** The deck's two edges at a distance along the bridge, interpolated. */
function edgesAt(P: Float32Array, s: Section[], d: number): Edge {
  let i = 0;
  while (i + 2 < s.length && s[i + 1].distance < d) i++;
  const a = s[i], b = s[Math.min(i + 1, s.length - 1)];
  const span = b.distance - a.distance;
  const t = span > 1e-6 ? Math.min(1, Math.max(0, (d - a.distance) / span)) : 0;
  const mix = (v0: number, v1: number) => v0 + (v1 - v0) * t;
  return {
    lx: mix(P[a.a * 3], P[b.a * 3]), ly: mix(P[a.a * 3 + 1], P[b.a * 3 + 1]), lz: mix(P[a.a * 3 + 2], P[b.a * 3 + 2]),
    rx: mix(P[(a.a + 1) * 3], P[(b.a + 1) * 3]), ry: mix(P[(a.a + 1) * 3 + 1], P[(b.a + 1) * 3 + 1]),
    rz: mix(P[(a.a + 1) * 3 + 2], P[(b.a + 1) * 3 + 2]),
  };
}

/** A widened deck's surface under the road's, metres: the road is drawn over where they meet. */
const WIDENED_UNDER = 0.02;

/** Unit direction on the ground from vertex `other` across to `v`: out from the deck at `v`'s edge. */
function outwardOf(P: Float32Array, v: number, other: number): [number, number] {
  const nx = P[v * 3] - P[other * 3], nz = P[v * 3 + 2] - P[other * 3 + 2];
  const nl = Math.hypot(nx, nz) || 1;
  return [nx / nl, nz / nl];
}

function buildStructure(
  out: { positions: number[]; colors: number[]; indices: number[] },
  P: Float32Array,
  bridge: Run,
  crossings: Crossing[],
  ground: (x: number, z: number) => number,
  decks: Decks,
  obstacles: Obstacles,
  /** Parapets to collide with, appended: see `BridgeStructure.walls`. */
  walls: number[] = [],
  railings?: RailingIndex,
  /** The widened decks, appended again on their own: see `BridgeStructure.widened`. */
  widened?: { positions: number[]; colors: number[]; indices: number[] },
): void {
  const s = bridge.sections;
  const length = s[s.length - 1].distance;
  const colour = bridge.style === ROAD_STYLE.rail ? STONE : CONCRETE;
  const dark = colour.map(c => c * 0.72);

  const quad = (a: number[], b: number[], c: number[], d: number[], col: number[]) => {
    const base = out.positions.length / 3;
    out.positions.push(...a, ...b, ...c, ...d);
    for (let k = 0; k < 4; k++) out.colors.push(...col);
    out.indices.push(base, base + 1, base + 2, base, base + 2, base + 3);
  };

  // Whether any of this bridge's edges has a mapped railing: where it does,
  // the gaps between them are left open as mapped, not given a parapet.
  const railed = railings !== undefined && !railings.empty && s.some((_, i) => [0, 1].some(side => {
    const v = s[i].a + side, other = 1 - 2 * side, o = outwardOf(P, v, v + other);
    return railings.outward(P[v * 3], P[v * 3 + 2], o[0], o[1]) !== undefined;
  }));

  // Parapets above the deck and fascia below it, along both edges — but no
  // parapet where another deck runs alongside: that side is decked over to
  // it instead, so a cluster of mapped bridges is one bridge.
  for (let i = 0; i + 1 < s.length; i++) {
    const ax = s[i + 1].x - s[i].x, az = s[i + 1].z - s[i].z;
    const along = Math.hypot(ax, az) || 1;
    // A tram's bare rails need a deck of their own, where they are not on another's.
    if (bridge.style === ROAD_STYLE.tram && decks.at((s[i].x + s[i + 1].x) / 2, (s[i].z + s[i + 1].z) / 2, bridge) === undefined) {
      const v0 = s[i].a, v1 = s[i + 1].a;
      const at = (v: number) => [P[v * 3], P[v * 3 + 1] - SLAB_UNDER, P[v * 3 + 2]];
      quad(at(v0), at(v1), at(v1 + 1), at(v0 + 1), SLAB);
    }
    for (const side of [0, 1]) {
      const v0 = s[i].a + side, v1 = s[i + 1].a + side;
      const p0 = [P[v0 * 3], P[v0 * 3 + 1], P[v0 * 3 + 2]];
      const p1 = [P[v1 * 3], P[v1 * 3 + 1], P[v1 * 3 + 2]];
      // How far out from each end of this edge the next deck begins.
      const beside = (v: number, other: number) => {
        const nx = P[v * 3] - P[other * 3], nz = P[v * 3 + 2] - P[other * 3 + 2];
        const nl = Math.hypot(nx, nz) || 1;
        for (let d = 0.25; d <= CLUSTER_GAP; d += 0.5) {
          const x = P[v * 3] + nx / nl * d, z = P[v * 3 + 2] + nz / nl * d;
          const hit = decks.at(x, z, bridge);
          if (hit === undefined) continue;
          // Side by side and level with it: not a deck crossing over or under.
          if (Math.abs(hit.h - P[v * 3 + 1]) > 1.2 || Math.abs(hit.dx * ax + hit.dz * az) / along < 0.8) return undefined;
          return [x, hit.h - SLAB_UNDER, z];
        }
        return undefined;
      };
      const other = 1 - 2 * side;
      const f0 = beside(v0, v0 + other), f1 = beside(v1, v1 + other);
      if (f0 !== undefined || f1 !== undefined) {
        const near0 = [p0[0], p0[1] - SLAB_UNDER, p0[2]], near1 = [p1[0], p1[1] - SLAB_UNDER, p1[2]];
        quad(near0, near1, f1 ?? near1, f0 ?? near0, SLAB);
        continue;
      }
      // Along this edge, half a metre at a time: where a mapped railing
      // stands off it, the deck out to it, its fascia there, and no parapet
      // of the game's own; where none does, the game's own, unless the
      // bridge's railings are mapped and this is a gap in them. Judged at the
      // edge's ends alone, a railing that stopped partway left the rest of
      // the edge with neither, and one that stopped just short of both ends
      // a concrete parapet beside it.
      const out0 = outwardOf(P, v0, v0 + other), out1 = outwardOf(P, v1, v1 + other);
      const at = (t: number) => [p0[0] + (p1[0] - p0[0]) * t, p0[1] + (p1[1] - p0[1]) * t, p0[2] + (p1[2] - p0[2]) * t];
      const outAt = (t: number): [number, number] => {
        const ox = out0[0] + (out1[0] - out0[0]) * t, oz = out0[1] + (out1[1] - out0[1]) * t, l = Math.hypot(ox, oz) || 1;
        return [ox / l, oz / l];
      };
      const mapped = (t: number) => {
        if (railings === undefined || railings.empty) return undefined;
        const q = at(t), o = outAt(t);
        return railings.outward(q[0], q[2], o[0], o[1]);
      };
      const edgeLength = Math.hypot(p1[0] - p0[0], p1[2] - p0[2]);
      const steps = railings === undefined || railings.empty ? 1 : Math.max(1, Math.ceil(edgeLength / RAILING_STEP));
      const reach = Array.from({ length: steps }, (_, k) => mapped((k + 0.5) / steps));
      for (let k = 0; k < steps;) {
        const covered = reach[k] !== undefined;
        let j = k + 1;
        while (j < steps && (reach[j] !== undefined) === covered) j++;
        const t0 = k / steps, t1 = j / steps;
        const a = at(t0), b = at(t1);
        // The deck out to `e0`, `e1` metres past the edge at each end, its fascia on the far side.
        const deckOut = (e0: number, e1: number) => {
          const o0 = outAt(t0), o1 = outAt(t1);
          const far0 = [a[0] + o0[0] * e0, a[1] - WIDENED_UNDER, a[2] + o0[1] * e0];
          const far1 = [b[0] + o1[0] * e1, b[1] - WIDENED_UNDER, b[2] + o1[1] * e1];
          const near0 = [a[0], a[1] - WIDENED_UNDER, a[2]], near1 = [b[0], b[1] - WIDENED_UNDER, b[2]];
          if (Math.max(e0, e1) > 0.2) {
            quad(near0, near1, far1, far0, colour);
            if (widened !== undefined) {
              const base = widened.positions.length / 3;
              widened.positions.push(...near0, ...near1, ...far1, ...far0);
              for (let n = 0; n < 4; n++) widened.colors.push(...colour);
              widened.indices.push(base, base + 1, base + 2, base, base + 2, base + 3);
            }
          }
          quad([far0[0], far0[1] - DECK_DEPTH, far0[2]], [far1[0], far1[1] - DECK_DEPTH, far1[2]], far1, far0, colour);
        };
        const road = bridge.style <= ROAD_STYLE.service;
        if (covered) deckOut(mapped(t0) ?? reach[k]!, mapped(t1) ?? reach[j - 1]!);
        else if (railed && !road) {
          // A gap between a footbridge's mapped railings is left open, as
          // mapped: where a stair or a path comes up onto it. The deck runs
          // on across it as wide as at the railings either side.
          const before = k > 0 ? reach[k - 1] : undefined, after = j < steps ? reach[j] : undefined;
          if (before !== undefined || after !== undefined) deckOut(before ?? after!, after ?? before!);
          else quad([a[0], a[1] - DECK_DEPTH, a[2]], [b[0], b[1] - DECK_DEPTH, b[2]], b, a, colour);
        } else {
          // A road's deck has a low edge beam and the guardrail on it
          // (`guardrails.ts`); a path's or a railway's, its parapet.
          if (road) quad(a, b, [b[0], b[1] + EDGE_BEAM, b[2]], [a[0], a[1] + EDGE_BEAM, a[2]], colour);
          else quad(a, b, [b[0], b[1] + PARAPET, b[2]], [a[0], a[1] + PARAPET, a[2]], colour);
          // Solid, so nobody walks off a footbridge or out of a railway's: from
          // under the deck to the parapet's top, set in by the wall's half
          // thickness so it stands on the deck's edge.
          if (!road) {
            const nx = P[(v0 + other) * 3] - p0[0], nz = P[(v0 + other) * 3 + 2] - p0[2], nl = Math.hypot(nx, nz) || 1;
            const ix = nx / nl * PARAPET_IN, iz = nz / nl * PARAPET_IN;
            walls.push(a[0] + ix, a[2] + iz, b[0] + ix, b[2] + iz, Math.min(a[1], b[1]) - DECK_DEPTH, Math.max(a[1], b[1]) + PARAPET);
          }
          quad([a[0], a[1] - DECK_DEPTH, a[2]], [b[0], b[1] - DECK_DEPTH, b[2]], b, a, colour);
        }
        k = j;
      }
    }
  }
  if (length < 6) return;

  // Span boundaries: the ends, a span centred over every crossing, and piers
  // no more than MAX_SPAN apart elsewhere — but never a pier standing in what
  // passes under the bridge. Each is tried where the layout puts it, then
  // moved along the deck to the nearest place it stands clear, or left out
  // and its span widened. Over mapped water with no centreline, none at all.
  const keepOut = crossings.filter(c => c.under < 0).map(c => [c.distance - c.spanHalf + 1, c.distance + c.spanHalf - 1] as const);
  const bounds = new Set<number>([0, length]);
  for (const c of crossings) {
    for (const b of [c.distance - c.spanHalf, c.distance + c.spanHalf]) if (b > 2 && b < length - 2) bounds.add(b);
  }
  const sorted = [...bounds].sort((a, b) => a - b);
  const all: number[] = [];
  for (let k = 0; k + 1 < sorted.length; k++) {
    const gap = sorted[k + 1] - sorted[k];
    const n = Math.max(1, Math.ceil(gap / MAX_SPAN));
    for (let m = 0; m < n; m++) all.push(sorted[k] + (gap * m) / n);
  }

  // Interior piers have thickness: each arch springs from a pier's face, not
  // from a knife-edge where two arches meet.
  const PIER_HALF = 0.9;
  // A slab bridge's piers turn to run alongside the road under them, as
  // they are built; a stone arch's stand square to its deck.
  const skewed = bridge.style !== ROAD_STYLE.rail;
  const piers: Pier[] = [{ d: 0, k: 0 }];
  for (let i = 1; i < all.length; i++) {
    const d = all[i];
    if (keepOut.some(([lo, hi]) => d > lo && d < hi)) continue;
    const last = piers[piers.length - 1].d;
    for (const off of PIER_SHIFTS) {
      const at = d + off;
      if (at - last < 5 || at > length - 5) continue;
      const k = skewed ? skewAt(P, s, length, at, PIER_HALF, obstacles, bridge) : 0;
      const foot = pierFootprint(P, s, at, k, PIER_HALF);
      if (obstacles.clear(foot.outline, foot.top - BENEATH, bridge, PIER_CLEAR + VERGE)) {
        piers.push({ d: at, k });
        break;
      }
    }
  }
  piers.push({ d: length, k: 0 });
  // A span left long because no pier stood clear near where the layout put
  // one: split it wherever one does, nearest its middle, until none can be.
  const tryAt = (at: number): Pier | undefined => {
    if (keepOut.some(([lo, hi]) => at > lo && at < hi)) return undefined;
    const k = skewed ? skewAt(P, s, length, at, PIER_HALF, obstacles, bridge) : 0;
    const foot = pierFootprint(P, s, at, k, PIER_HALF);
    return obstacles.clear(foot.outline, foot.top - BENEATH, bridge, PIER_CLEAR + VERGE) ? { d: at, k } : undefined;
  };
  for (let i = 0; i + 1 < piers.length; i++) {
    const a = piers[i].d, b = piers[i + 1].d;
    if (b - a <= MAX_SPAN * 1.5) continue;
    const mid = (a + b) / 2;
    for (let off = 0; off < (b - a) / 2 - 5; off = off > 0 ? -off : -off + 1) {
      const found = tryAt(mid + off);
      if (found !== undefined) { piers.splice(i + 1, 0, found); i--; break; }
    }
  }

  if (bridge.style !== ROAD_STYLE.rail) {
    slab(quad, P, s, length, ground, colour, dark, obstacles, bridge, crossings.some(c => c.underpass === true));
    piersOf(quad, P, s, piers, PIER_HALF, ground, colour);
    return;
  }
  for (let k = 0; k + 1 < piers.length; k++) {
    const s0 = piers[k].d + (k > 0 ? PIER_HALF : 0);
    const s1 = piers[k + 1].d - (k + 2 < piers.length ? PIER_HALF : 0);
    const span = s1 - s0;
    if (span < 2) continue;
    const e0 = edgesAt(P, s, s0);
    const e1 = edgesAt(P, s, s1);
    const g0 = ground((e0.lx + e0.rx) / 2, (e0.lz + e0.rz) / 2);
    const g1 = ground((e1.lx + e1.rx) / 2, (e1.lz + e1.rz) / 2);
    const deckLow = Math.min(e0.ly, e0.ry, e1.ly, e1.ry) - DECK_DEPTH;
    const low = Math.min(g0, g1);
    // Not worth an arch when the deck sits on the ground.
    if (deckLow - low < 1.2) continue;
    // Spring the arch from the ground, or from a pier high enough that the
    // arch is no taller than half its span — a viaduct's tall piers.
    const spring = Math.max(low, deckLow - span * 0.5);

    const steps = 10;
    let prev: { edge: Edge; y: number } | undefined;
    for (let j = 0; j <= steps; j++) {
      const t = j / steps;
      const edge = edgesAt(P, s, s0 + span * t);
      const deckBottom = (edge.ly + edge.ry) / 2 - DECK_DEPTH;
      const shape = Math.sqrt(Math.max(0, 1 - (2 * t - 1) ** 2));
      const y = spring + (deckBottom - 0.2 - spring) * shape;
      if (prev !== undefined) {
        const a = prev.edge, b = edge;
        const aTop = (a.ly + a.ry) / 2 - DECK_DEPTH, bTop = deckBottom;
        // Spandrel walls, deck-side down to the arch.
        quad([a.lx, prev.y, a.lz], [b.lx, y, b.lz], [b.lx, bTop, b.lz], [a.lx, aTop, a.lz], colour);
        quad([a.rx, aTop, a.rz], [b.rx, bTop, b.rz], [b.rx, y, b.rz], [a.rx, prev.y, a.rz], colour);
        // The soffit: the underside of the arch, shadowed.
        quad([a.lx, prev.y, a.lz], [a.rx, prev.y, a.rz], [b.rx, y, b.rz], [b.lx, y, b.lz], dark);
      }
      prev = { edge, y };
    }

  }

  piersOf(quad, P, s, piers, PIER_HALF, ground, colour);
}

type Quad = (a: number[], b: number[], c: number[], d: number[], col: number[]) => void;

/**
 * A pier at `d` along the deck, its middle. Skewed by `k`: its end under the
 * deck's left edge is `k` metres back along the deck and its right end `k`
 * on, so it lies along a road crossing under at an angle.
 */
interface Pier { d: number; k: number }

/** Where a pier stands: its outline on the ground, x, z corners, and the lowest of the deck's underside over it. */
function pierFootprint(P: Float32Array, s: Section[], d: number, k: number, pierHalf: number): { outline: Array<[number, number]>; top: number; ring: Array<[number, number, number]> } {
  const a = edgesAt(P, s, d - k - pierHalf), b = edgesAt(P, s, d - k + pierHalf);
  const c = edgesAt(P, s, d + k + pierHalf), e = edgesAt(P, s, d + k - pierHalf);
  const ring: Array<[number, number, number]> = [
    [a.lx, a.lz, a.ly - DECK_DEPTH], [b.lx, b.lz, b.ly - DECK_DEPTH], [c.rx, c.rz, c.ry - DECK_DEPTH], [e.rx, e.rz, e.ry - DECK_DEPTH],
  ];
  return { outline: ring.map(([x, z]) => [x, z]), top: Math.min(...ring.map(r => r[2])), ring };
}

/**
 * How far to skew a pier at `d` (see `Pier`) to lie along the nearest thing
 * passing under the deck there, or 0 with nothing near. Never more than a
 * crossing at 30° asks, nor past the deck's ends.
 */
function skewAt(P: Float32Array, s: Section[], length: number, d: number, pierHalf: number, obstacles: Obstacles, self: Run): number {
  const mid = edgesAt(P, s, d);
  const cx = (mid.lx + mid.rx) / 2, cz = (mid.lz + mid.rz) / 2;
  const nx = mid.rx - mid.lx, nz = mid.rz - mid.lz, w = Math.hypot(nx, nz);
  if (w < 1e-6) return 0;
  const near = obstacles.nearest(cx, cz, 15, (mid.ly + mid.ry) / 2 - DECK_DEPTH - BENEATH, self);
  if (near === undefined) return 0;
  const tx = nz / w, tz = -nx / w;
  // Along the deck, the same way as its distance runs.
  const ahead = edgesAt(P, s, Math.min(length, d + 0.5)), back = edgesAt(P, s, Math.max(0, d - 0.5));
  const sign = Math.sign(((ahead.lx + ahead.rx) - (back.lx + back.rx)) * tx + ((ahead.lz + ahead.rz) - (back.lz + back.rz)) * tz) || 1;
  const un = (near.dx * nx + near.dz * nz) / w, ut = sign * (near.dx * tx + near.dz * tz);
  const across = Math.max(0.5, Math.abs(un)) * (Math.sign(un) || 1);
  let k = (w / 2) * ut / across;
  const room = Math.max(0, Math.min(d, length - d) - pierHalf - 0.5);
  k = Math.max(-room, Math.min(room, k));
  return Math.abs(k) < 0.3 ? 0 : k;
}

/** Piers: solid blocks from below the ground up to the deck, one at every interior span boundary. */
function piersOf(
  quad: Quad, P: Float32Array, s: Section[], piers: Pier[], pierHalf: number,
  ground: (x: number, z: number) => number, colour: number[],
): void {
  for (let k = 1; k + 1 < piers.length; k++) {
    const { ring, top } = pierFootprint(P, s, piers[k].d, piers[k].k, pierHalf);
    // Down to the lowest ground under it, so a skewed pier on a slope is not left standing on one corner.
    const g = Math.min(...ring.map(([x, z]) => ground(x, z)));
    if (top - g < 0.6) continue;
    for (let c = 0; c < 4; c++) {
      const [x0, z0, t0] = ring[c];
      const [x1, z1, t1] = ring[(c + 1) % 4];
      quad([x0, g - 0.6, z0], [x1, g - 0.6, z1], [x1, t1, z1], [x0, t0, z0], colour);
    }
  }
}

/**
 * Everything a pier must not stand in, as centreline segments with their
 * half-widths and heights, hashed. Asked only about what is beneath a deck:
 * a road at the deck's own level beside a pier is its approach.
 */
class Obstacles {
  private readonly cells = new Map<number, Array<{ run: Run; x0: number; z0: number; x1: number; z1: number; y: number }>>();
  private static readonly CELL = 10;

  constructor(list: Array<{ run: Run; positions: Float32Array }>) {
    const C = Obstacles.CELL;
    for (const { run, positions } of list) {
      const s = run.sections;
      for (let i = 0; i + 1 < s.length; i++) {
        const seg = { run, x0: s[i].x, z0: s[i].z, x1: s[i + 1].x, z1: s[i + 1].z, y: Math.min(heightAt(positions, s[i].a), heightAt(positions, s[i + 1].a)) };
        const r = run.halfWidth + VERGE + PIER_CLEAR;
        for (let cx = Math.floor((Math.min(seg.x0, seg.x1) - r) / C); cx <= Math.floor((Math.max(seg.x0, seg.x1) + r) / C); cx++) {
          for (let cz = Math.floor((Math.min(seg.z0, seg.z1) - r) / C); cz <= Math.floor((Math.max(seg.z0, seg.z1) + r) / C); cz++) {
            const key = cellKey(cx, cz);
            let cell = this.cells.get(key);
            if (cell === undefined) this.cells.set(key, (cell = []));
            cell.push(seg);
          }
        }
      }
    }
  }

  private near(x0: number, z0: number, x1: number, z1: number, under: number, self: Run) {
    const C = Obstacles.CELL;
    const found = new Set<{ run: Run; x0: number; z0: number; x1: number; z1: number; y: number }>();
    for (let cx = Math.floor(Math.min(x0, x1) / C); cx <= Math.floor(Math.max(x0, x1) / C); cx++) {
      for (let cz = Math.floor(Math.min(z0, z1) / C); cz <= Math.floor(Math.max(z0, z1) / C); cz++) {
        for (const seg of this.cells.get(cellKey(cx, cz)) ?? []) if (seg.run !== self && seg.y < under) found.add(seg);
      }
    }
    return found;
  }

  /** Whether the outline keeps `clearance` past the edge of everything lower than `under`. */
  clear(outline: Array<[number, number]>, under: number, self: Run, clearance: number): boolean {
    const xs = outline.map(p => p[0]), zs = outline.map(p => p[1]);
    for (const seg of this.near(Math.min(...xs), Math.min(...zs), Math.max(...xs), Math.max(...zs), under, self)) {
      if (polygonToSegment(outline, seg.x0, seg.z0, seg.x1, seg.z1) < seg.run.halfWidth + clearance) return false;
    }
    return true;
  }

  /** The direction of the nearest thing lower than `under` within `radius` of (x, z), past its edge. */
  nearest(x: number, z: number, radius: number, under: number, self: Run): { dx: number; dz: number } | undefined {
    let best: { dx: number; dz: number } | undefined, bestD = radius;
    for (const seg of this.near(x - radius, z - radius, x + radius, z + radius, under, self)) {
      const len = Math.hypot(seg.x1 - seg.x0, seg.z1 - seg.z0);
      if (len < 1e-6) continue;
      const d = pointToSegment(x, z, seg.x0, seg.z0, seg.x1, seg.z1) - seg.run.halfWidth;
      if (d < bestD) { bestD = d; best = { dx: (seg.x1 - seg.x0) / len, dz: (seg.z1 - seg.z0) / len }; }
    }
    return best;
  }
}

function pointToSegment(x: number, z: number, ax: number, az: number, bx: number, bz: number): number {
  const ex = bx - ax, ez = bz - az, len2 = ex * ex + ez * ez;
  const t = len2 > 0 ? Math.max(0, Math.min(1, ((x - ax) * ex + (z - az) * ez) / len2)) : 0;
  return Math.hypot(x - ax - ex * t, z - az - ez * t);
}

/** Distance from a convex polygon (x, z corners in order) to a segment: 0 where they touch. */
function polygonToSegment(poly: Array<[number, number]>, ax: number, az: number, bx: number, bz: number): number {
  const inside = (x: number, z: number) => {
    let sign = 0;
    for (let i = 0; i < poly.length; i++) {
      const [x0, z0] = poly[i], [x1, z1] = poly[(i + 1) % poly.length];
      const c = (x1 - x0) * (z - z0) - (z1 - z0) * (x - x0);
      if (Math.abs(c) < 1e-9) continue;
      if (sign === 0) sign = Math.sign(c);
      else if (Math.sign(c) !== sign) return false;
    }
    return true;
  };
  if (inside(ax, az) || inside(bx, bz)) return 0;
  let best = Infinity;
  for (let i = 0; i < poly.length; i++) {
    const [x0, z0] = poly[i], [x1, z1] = poly[(i + 1) % poly.length];
    if (intersect(x0, z0, x1, z1, ax, az, bx, bz) !== undefined) return 0;
    best = Math.min(best, pointToSegment(x0, z0, ax, az, bx, bz), pointToSegment(ax, az, x0, z0, x1, z1), pointToSegment(bx, bz, x0, z0, x1, z1));
  }
  return best;
}

/**
 * A concrete slab bridge: the deck's flat underside over every span where it
 * stands clear of the ground, and at each end an abutment, a wall from the
 * ground up to the deck, with a wing wall back along each side holding the
 * embankment, falling with it to the ground.
 */
function slab(
  quad: Quad, P: Float32Array, s: Section[], length: number,
  ground: (x: number, z: number) => number, colour: number[], dark: number[],
  obstacles: Obstacles, self: Run,
  /**
   * Over an underpass: no abutments or wing walls. The trough's walls hold
   * the bank under the deck (`tunnelmesh.ts`); square to a road crossing it
   * at a slant, its own cut through them in a cross.
   */
  overUnderpass = false,
): void {
  // The underside, section by section, wherever it is above the ground.
  const step = 2;
  for (let d = 0; d < length; d += step) {
    const a = edgesAt(P, s, d), b = edgesAt(P, s, Math.min(length, d + step));
    const ga = ground((a.lx + a.rx) / 2, (a.lz + a.rz) / 2);
    const ya = (a.ly + a.ry) / 2 - DECK_DEPTH, yb = (b.ly + b.ry) / 2 - DECK_DEPTH;
    if (ya - ga < 0.3) continue;
    quad([a.lx, ya, a.lz], [a.rx, ya, a.rz], [b.rx, yb, b.rz], [b.lx, yb, b.lz], dark);
  }
  // Abutments, at the ends: facing into the span, the deck bearing on them.
  for (const end of overUnderpass ? [] : [0, length]) {
    const e = edgesAt(P, s, end);
    const inward = edgesAt(P, s, end === 0 ? Math.min(length, 1) : Math.max(0, length - 1));
    const top = (e.ly + e.ry) / 2 - DECK_DEPTH;
    const g = Math.min(ground(e.lx, e.lz), ground(e.rx, e.rz));
    if (top - g < 1.2) continue;
    // Out a little past the deck's edges, as the wall is wider than the road.
    const wx = e.rx - e.lx, wz = e.rz - e.lz, wl = Math.hypot(wx, wz) || 1;
    const lx = e.lx - (wx / wl) * 0.4, lz = e.lz - (wz / wl) * 0.4;
    const rx = e.rx + (wx / wl) * 0.4, rz = e.rz + (wz / wl) * 0.4;
    // A deck mapped short ends over the road it crosses: no wall across that
    // road, the deck rests on its approach instead.
    if (!obstacles.clear([[lx, lz], [rx, rz], [inward.rx, inward.rz], [inward.lx, inward.lz]], top - BENEATH, self, 0)) continue;
    quad([lx, g - 0.6, lz], [rx, g - 0.6, rz], [rx, top, rz], [lx, top, lz], colour);
    // Wing walls: back from each corner, away from the span and splayed a
    // little out, down the bank to the ground.
    const bx = e.lx + e.rx - inward.lx - inward.rx, bz = e.lz + e.rz - inward.lz - inward.rz;
    const bl = Math.hypot(bx, bz) || 1;
    const back = Math.min(8, (top - g) * 1.5 + 0.5);
    for (const [cx, cz, side] of [[lx, lz, -1], [rx, rz, 1]] as const) {
      const fx = cx + (bx / bl) * back + (wx / wl) * side * back * 0.35;
      const fz = cz + (bz / bl) * back + (wz / wl) * side * back * 0.35;
      const gf = ground(fx, fz);
      quad([cx, g - 0.6, cz], [fx, gf - 0.6, fz], [fx, gf + 0.3, fz], [cx, top + DECK_DEPTH, cz], colour);
    }
  }
}
